import { useRef, useState } from 'preact/hooks';
import { media, videoChapters } from '@goalpost/content';
import type { VideoChapter } from '@goalpost/content';
import { useTheme } from '../../hooks/useTheme.tsx';

interface DemoPlayerProps {
  // Base media id without the theme suffix or extension, e.g.
  // `product-demo-software`. Resolves like MediaVideo — the no-burn
  // `{base}-{light|dark}-cc.mp4` cut, its `.vtt` track, and its poster — plus
  // the composition's chapter list from the video-chapters manifest.
  base: string;
  // Accessible description of what the video shows.
  label: string;
  // Heading and accessible name for the chapter navigation rail.
  chaptersLabel: string;
  // Title of the implicit opening chapter at 0:00 — the emitted manifest only
  // carries the numbered chapter cards, and the cut opens with a welcome
  // sequence before the first one.
  introTitle: string;
}

const formatTime = (ms: number): string => {
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

// The /demo page's player: the full product demo with a chapter rail beside
// it, because a browser <video> has no native chapter menu and an
// eight-minute embed isn't navigable without one. Clicking a chapter seeks
// (and starts playback if paused); the active chapter tracks playback so the
// rail doubles as a progress indicator. Like MediaVideo, renders nothing
// until the cut has been synced, and the rail renders only once a chapter
// manifest for the cut has been synced too.
export function DemoPlayer({ base, label, chaptersLabel, introTitle }: DemoPlayerProps) {
  const { theme } = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const preferred = theme === 'graphite' ? 'dark' : 'light';
  const fallback = preferred === 'dark' ? 'light' : 'dark';
  const preferredVideo = media(`${base}-${preferred}-cc.mp4`);
  const video = preferredVideo ?? media(`${base}-${fallback}-cc.mp4`);
  if (!video) return null;

  const variant = preferredVideo ? preferred : fallback;
  const captions = media(`${base}-${variant}.vtt`);
  const poster = media(`${base}-${variant}-poster.webp`);
  const emitted = videoChapters(`${base}-${variant}`);
  const chapters: VideoChapter[] =
    emitted !== null ? [{ title: introTitle, startMs: 0 }, ...emitted] : [];

  // The last chapter whose start the playhead has passed. timeupdate fires a
  // few times a second; identical setState values bail out of re-rendering.
  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (el === null || chapters.length === 0) return;
    const ms = el.currentTime * 1000;
    let index = 0;
    for (let i = 0; i < chapters.length; i += 1) {
      if (chapters[i].startMs <= ms) index = i;
    }
    setActiveIndex(index);
  };

  const seekTo = (chapter: VideoChapter, index: number) => {
    const el = videoRef.current;
    if (el === null) return;
    el.currentTime = chapter.startMs / 1000;
    setActiveIndex(index);
    // A chapter click means "watch from here": start playback when paused.
    // play() can reject (e.g. an autoplay policy) — the seek already landed,
    // so the user just presses play themselves.
    if (el.paused) el.play().catch(() => {});
  };

  return (
    <figure className={`mkt-demo-player${chapters.length > 0 ? ' has-chapters' : ''}`}>
      {/*
        Cross-origin media + text track need CORS opt-in exactly when there's
        a track to load — same reasoning as MediaVideo (see its comment and
        docs/media-storage.md).
      */}
      <video
        ref={videoRef}
        className="mkt-demo-video"
        controls
        preload="metadata"
        playsInline
        crossOrigin={captions ? 'anonymous' : undefined}
        aria-label={label}
        poster={poster?.url}
        data-gp-name="demo-video"
        onTimeUpdate={handleTimeUpdate}
      >
        <source src={video.url} type={video.contentType} />
        {captions && (
          <track kind="captions" src={captions.url} srcLang="en" label="English" default />
        )}
      </video>
      {chapters.length > 0 && (
        <nav className="mkt-demo-chapters" aria-label={chaptersLabel} data-gp-name="demo-chapters">
          <span className="mkt-demo-chapters-heading">{chaptersLabel}</span>
          <ol className="mkt-demo-chapter-list">
            {chapters.map((chapter, index) => (
              <li key={chapter.startMs}>
                <button
                  type="button"
                  className={`mkt-demo-chapter${index === activeIndex ? ' active' : ''}`}
                  aria-current={index === activeIndex ? 'true' : undefined}
                  data-gp-name={`chapter-${index}`}
                  onClick={() => seekTo(chapter, index)}
                >
                  <span className="mkt-demo-chapter-title">
                    {chapter.number !== undefined ? `${chapter.number}. ` : ''}
                    {chapter.title}
                  </span>
                  <span className="mkt-demo-chapter-time">{formatTime(chapter.startMs)}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      )}
    </figure>
  );
}
